import { type ComponentType, forwardRef, useId } from 'react';
import type { IsNever } from '@direct-frontend/stdlib';
import {
    type BaseFields,
    type BaseFormActions,
    type BaseValidationError,
    Form,
    type FormDeclaration,
    type InferFieldsValue,
} from '@adv-frontend/form-kit';

type EnhancedComponentProps<TProps extends object, FormFields extends BaseFields, FormContextInput> = TProps &
    IsNever<FormContextInput, {}, { context: FormContextInput }> & {
        initialValue?: InferFieldsValue<FormFields>;
    };

export function withFormContext<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends BaseFormActions,
    FormSubmitArg,
    FormSubmitReturnValue,
>(
    form: FormDeclaration<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >,
    formIdProp?: string
) {
    return function <TProps extends object>(BaseComponent: ComponentType<TProps & { formId: string }>) {
        return forwardRef(function WithFormContext(
            props: EnhancedComponentProps<TProps, FormFields, FormContextInput>,
            ref
        ) {
            const generatedFormId = useId();
            const formId = formIdProp || generatedFormId;
            const { initialValue } = props;
            const context = ('context' in props ? props.context : undefined) as FormContextInput;

            return (
                <Form form={form} id={formId} initialValue={initialValue} context={context}>
                    <BaseComponent ref={ref} {...props} formId={formId} />
                </Form>
            );
        });
    };
}
